import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';

declare global {
  interface Window {
    utag_data?: Record<string, any>;
    upsNotifyViewChange?: (dataObject: { viewName: string; page?: boolean }) => void;
    trackPageViewOrAction?: (dataObject: any) => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class TealiumLoaderService {
  private renderer: Renderer2;
  private isLoaded = false;
  private isLoading = false;

  // Replace <profile> and <env> dynamically or via environment.ts
  private readonly profile = 'maestro'; // Confirm profile with MarTech
  private readonly env = 'dev';        // Use environment.production ? 'prod' : 'dev'

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * Dynamically loads utag.sync.js and utag.js into document head
   */
  public loadTealium(): Promise<void> {
    if (this.isLoaded) {
      return Promise.resolve();
    }
    if (this.isLoading) {
      return this.waitForTealiumReady();
    }

    this.isLoading = true;

    // 1. Initialize UDO if not present
    window.utag_data = window.utag_data || {
      page_id: 'rewards_account_dashboard',
      page_name: 'Rewards Account Dashboard',
      site_area: 'Rewards_Dashboard',
      site_sub_area: 'Rewards_Dashboard'
    };

    // 2. Inject utag.sync.js (Adobe Target synchronous bridge)
    const syncScript = this.renderer.createElement('script');
    syncScript.src = `//tags.tiqcdn.com/utag/ups/${this.profile}/${this.env}/utag.sync.js`;
    syncScript.type = 'text/javascript';
    this.renderer.appendChild(document.head, syncScript);

    // 3. Inject utag.js container
    const utagScript = this.renderer.createElement('script');
    utagScript.src = `//tags.tiqcdn.com/utag/ups/${this.profile}/${this.env}/utag.js`;
    utagScript.type = 'text/javascript';
    utagScript.async = true;

    return new Promise((resolve, reject) => {
      utagScript.onload = () => {
        this.isLoaded = true;
        this.isLoading = false;
        resolve();
      };
      utagScript.onerror = (err: any) => {
        this.isLoading = false;
        console.error('[TealiumLoader] Failed to load utag.js', err);
        reject(err);
      };
      this.renderer.appendChild(document.head, utagScript);
    });
  }

  /**
   * Fires view changes to Adobe Target and Tealium only if on NextGen
   */
  public notifyViewChange(viewName: string, page: boolean = true): void {
    if (window.upsNotifyViewChange && typeof window.upsNotifyViewChange === 'function') {
      window.upsNotifyViewChange({ viewName, page });
    }
  }

  public trackAction(data: Record<string, any>, actionType: 'view' | 'link' = 'view'): void {
    if (window.trackPageViewOrAction && typeof window.trackPageViewOrAction === 'function') {
      window.trackPageViewOrAction({
        data,
        actionType,
        callback: () => {}
      });
    }
  }

  private waitForTealiumReady(): Promise<void> {
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        if (this.isLoaded) {
          clearInterval(interval);
          resolve();
        }
      }, 50);
    });
  }
}